import { Assets, Components, Enums, Types } from '@sendum/core'
import * as Utils from '@sendum/core/src/utils'
import React, { useCallback, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Column, useTable } from 'react-table'

import * as ShipmentSelectorActions from '../store/ShipmentSelectorActions'
import { ShipmentSelectorRootState } from '../store/ShipmentSelectorStore'
import { TFilter } from '../types/TFilter'
import s from './ShipmentSelectorTable.module.scss'

const columns: Column<Types.TSmallShipment>[] = [
    { Header: 'Master Tracking Number', accessor: 'masterTrackingNo', width: 180 },
    { Header: 'Client', accessor: 'name', width: 180 },
    { Header: 'Container', accessor: 'containerId', width: 180 },
    { Header: 'Device Id', accessor: 'deviceId', width: 180 },
    {
        Header: 'Start Date',
        accessor: item => Utils.time.format(+item.startDate, 'DD MMM YYYY HH:mm'),
        width: 180,
        id: 'startDate'
    },
    { Header: 'Placement Site', accessor: 'origin', width: 180 },
    { Header: 'Kite Site', accessor: 'kiteSite', width: 180 },
    { Header: 'Infusion Site', accessor: 'infusionSite', width: 180 },
    { Header: 'Return Site', accessor: 'destination', width: 180 }
]

interface IProps {
    shipments?: Types.TSmallShipment[]
    total?: number
    isLoading?: boolean
    onShipmentClick?: (shipmentId: string) => void
    onChange: () => void
}

export const ShipmentSelectorTable = React.memo(
    ({ shipments, isLoading, onShipmentClick, onChange }: IProps): JSX.Element => {
        const dispatch = useDispatch()

        const data = useMemo(() => (shipments ? [...shipments] : []), [shipments])
        const filter = useSelector<ShipmentSelectorRootState, TFilter>(
            (state: ShipmentSelectorRootState) => state.ShipmentSelector.filter,
            shallowEqual
        )

        const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
            columns,
            data
        })

        const onClickRowHandler = useCallback(
            (masterTrackingNo: string) => () => {
                if (onShipmentClick) {
                    onShipmentClick(masterTrackingNo)
                }
            },
            [onShipmentClick]
        )

        const onClickSortBy = useCallback(
            (accessor: string, order: Enums.SortDirection) => {
                dispatch(
                    ShipmentSelectorActions.setFilter({
                        ...filter,
                        sortOrder: order,
                        sortBy: accessor.charAt(0).toUpperCase() + accessor.slice(1)
                    })
                )
                onChange()
            },
            [dispatch, filter, onChange]
        )

        return (
            <div className={s.shipmentSelectorTable}>
                {isLoading && (
                    <div className={s.loadingContainer}>
                        <Components.Loader text="Loading" className={s.loading} />
                    </div>
                )}

                <table {...getTableProps()} data-testid="shipment-selector-table">
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()} key={column.getHeaderProps().key}>
                                        <div className={s.hCell}>
                                            <span>{column.render('Header')}</span>
                                            <div>
                                                <div
                                                    className={s.icon}
                                                    onClick={() => onClickSortBy(column.id, Enums.SortDirection.ASC)}
                                                >
                                                    <img
                                                        className={
                                                            filter.sortBy?.toLowerCase() === column.id.toLowerCase() &&
                                                            filter.sortOrder === Enums.SortDirection.ASC
                                                                ? s.active
                                                                : s.inactive
                                                        }
                                                        src={Assets.ArrowUp}
                                                        alt={''}
                                                    />
                                                </div>
                                                <div
                                                    className={s.icon}
                                                    onClick={() => onClickSortBy(column.id, Enums.SortDirection.DESC)}
                                                >
                                                    <img
                                                        className={
                                                            filter.sortBy?.toLowerCase() === column.id.toLowerCase() &&
                                                            filter.sortOrder === Enums.SortDirection.DESC
                                                                ? s.active
                                                                : s.inactive
                                                        }
                                                        src={Assets.ArrowDown}
                                                        alt={''}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {!isLoading && rows.length === 0 && (
                            <tr className={s.noData}>
                                <td colSpan={100}>
                                    <div>No Data.</div>
                                </td>
                            </tr>
                        )}

                        {rows.length !== 0 &&
                            rows.map(row => {
                                prepareRow(row)

                                return (
                                    <tr
                                        {...row.getRowProps()}
                                        key={row.getRowProps().key}
                                        onClick={onClickRowHandler(row.original.masterTrackingNo)}
                                    >
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()} key={cell.getCellProps().key}>
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                )
                            })}
                    </tbody>
                </table>
            </div>
        )
    }
)
